import { Trans } from "@lingui/react/macro";
import dayjs from "dayjs";
import { RiPauseCircleLine } from "react-icons/ri";
import {
  type CalendarShift,
  type CalendarTimeOff,
  useCalendarStore,
} from "../../store/useCalendarStore";
import { useDraggable } from "@dnd-kit/core";
import { resolveTimeOffTypeName } from "@/pages/Dashboard/Schedule/Components/TimeOffTypes/timeOffTypeDisplayName";
import { resolveDayAbsence } from "../hooks/dayAbsence";
import { timeAtColumnOffset } from "../hooks/timeOffForm";
import { useOpenTimeOff } from "../hooks/useOpenTimeOff";
import { Tooltip } from "@heroui/react";

const timeoffTimeClassName = "text-[12px] leading-[12px]";

/** A client-made type may carry no colour of its own. */
const UNTYPED_COLOR = "#969BA9";

export function TimeoffColumn({
  timeoffs,
  date,
  shifts = [],
}: {
  timeoffs: CalendarTimeOff[];
  date: string;
  /** The column's shifts that day, which decide whether an absence is whole. */
  shifts?: CalendarShift[];
}) {
  const calendarData = useCalendarStore((state) => state.calendarData);
  const { slotHeight, timeStep, startTime, endTime, calendarHeight } =
    calendarData;

  const dateStartTime = dayjs(`${date} ${startTime}`).format(
    "YYYY-MM-DD HH:mm:ss",
  );
  const dateEndTime = dayjs(`${date} ${endTime}`).format("YYYY-MM-DD HH:mm:ss");

  const dayRows = (timeoffs ?? []).filter(
    (timeoff) => timeoff.typeGranularity === "DAY",
  );
  const blocks = (timeoffs ?? []).filter(
    (timeoff) => timeoff.typeGranularity !== "DAY",
  );
  const absence = resolveDayAbsence(dayRows, shifts, date);

  function placeOf(row: CalendarTimeOff) {
    return placeWithinDay(row, {
      dateStartTime,
      dateEndTime,
      slotHeight,
      timeStep,
    });
  }

  return (
    <>
      {absence?.kind === "whole" && (
        <DayAbsenceOverlay
          timeoff={absence.rows[0]}
          date={date}
          top={0}
          height={calendarHeight}
          isWholeDay
        />
      )}
      {absence?.kind === "part" &&
        absence.rows.map((row) => (
          <DayAbsenceOverlay
            key={`${row.source}-${row.id}`}
            timeoff={row}
            date={date}
            {...placeOf(row)}
            isWholeDay={false}
          />
        ))}
      {blocks.map((timeoff) => (
        <TimeoffBlock
          key={`${timeoff.source}-${timeoff.id}`}
          timeoff={timeoff}
          date={date}
          dayStartTime={startTime}
          dateStartTime={dateStartTime}
          dateEndTime={dateEndTime}
          slotHeight={slotHeight}
          timeStep={timeStep}
        />
      ))}
    </>
  );
}

function placeWithinDay(
  timeoff: CalendarTimeOff,
  day: {
    dateStartTime: string;
    dateEndTime: string;
    slotHeight: number;
    timeStep: number;
  },
) {
  const start = dayjs.max(dayjs(timeoff.timeFrom), dayjs(day.dateStartTime));
  const end = dayjs.min(dayjs(timeoff.timeTo), dayjs(day.dateEndTime));
  const top = Math.round(
    (dayjs(start).diff(dayjs(day.dateStartTime), "minute") / day.timeStep) *
      day.slotHeight,
  );
  const height = Math.round(
    (Math.max(dayjs(end).diff(dayjs(start), "minute"), 0) / day.timeStep) *
      day.slotHeight,
  );
  return { top, height };
}

function typeLabel(timeoff: CalendarTimeOff) {
  // A resource row carries no type at all, only the title it was created with.
  return (
    resolveTimeOffTypeName({
      name: timeoff.typeName,
      systemKey: timeoff.typeSystemKey,
    }) ||
    timeoff.title ||
    ""
  );
}

// Clicks fall through to the slot underneath; only the label opens the absence.
function DayAbsenceOverlay({
  timeoff,
  date,
  top,
  height,
  isWholeDay,
}: {
  timeoff: CalendarTimeOff;
  date: string;
  top: number;
  height: number;
  isWholeDay: boolean;
}) {
  const color = timeoff.typeColor ?? UNTYPED_COLOR;
  const name = typeLabel(timeoff);
  const from = timeoff.timeFrom.slice(11, 16);
  const to = timeoff.timeTo.slice(11, 16);
  const openTimeOff = useOpenTimeOff();

  return (
    <div
      className="pointer-events-none absolute left-0 w-full border-l-3"
      data-identifier={
        isWholeDay ? "time-off-day-hatch-whole" : "time-off-day-hatch-part"
      }
      style={{
        top,
        height,
        borderColor: color,
        background: `repeating-linear-gradient(135deg, ${color}2e 0 6px, transparent 6px 14px)`,
      }}
    >
      <button
        type="button"
        data-identifier="time-off-day-label"
        className="pointer-events-auto relative z-20 m-1 flex max-w-[calc(100%-8px)] items-center gap-1 truncate rounded-md px-1.5 py-0.5 text-left font-semibold text-[11px] text-white shadow-sm"
        style={{ backgroundColor: color }}
        onClick={(event) => {
          // The column underneath opens a new appointment on click.
          event.stopPropagation();
          openTimeOff(timeoff, date);
        }}
      >
        {timeoff.typeEmoji && (
          <span className="shrink-0">{timeoff.typeEmoji}</span>
        )}
        <span className="truncate">
          {isWholeDay ? (
            <Trans>{name} · ves dan</Trans>
          ) : (
            <Trans>
              {name} · {from}–{to}
            </Trans>
          )}
        </span>
      </button>
      {timeoff.note && (
        <p
          data-identifier="time-off-day-note"
          className="relative z-20 mx-1.5 line-clamp-3 break-words font-medium text-[#969BA9] text-xs"
        >
          {timeoff.note}
        </p>
      )}
    </div>
  );
}

function TimeoffBlock({
  timeoff,
  date,
  dayStartTime,
  dateStartTime,
  dateEndTime,
  slotHeight,
  timeStep,
}: {
  timeoff: CalendarTimeOff;
  date: string;
  dayStartTime: string;
  dateStartTime: string;
  dateEndTime: string;
  slotHeight: number;
  timeStep: number;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `timeoff-${timeoff.source}-${timeoff.id}-${date}`,
    data: {
      timeOff: {
        id: timeoff.id,
        source: timeoff.source,
        seriesId: timeoff.seriesId,
        originDate: dayjs(timeoff.timeFrom).format("YYYY-MM-DD"),
        originUserId: timeoff.userId,
        originResourceId: timeoff.resourceId,
      },
    },
  });

  const timeoffStart = dayjs
    .max(dayjs(timeoff.timeFrom), dayjs(dateStartTime))
    ?.format("YYYY-MM-DD HH:mm:ss");

  const timeoffEnd = dayjs
    .min(dayjs(timeoff.timeTo), dayjs(dateEndTime))
    ?.format("YYYY-MM-DD HH:mm:ss");

  const offset = Math.round(
    (dayjs(timeoffStart).diff(dayjs(dateStartTime), "minute") / timeStep) *
      slotHeight,
  );

  const timeOffDuration = dayjs(timeoffEnd).diff(dayjs(timeoffStart), "minute");
  const height = Math.round((timeOffDuration / timeStep) * slotHeight);

  // While a drag is in flight the block names the slot it would land in, using
  // the same conversion the drop does, so the label cannot promise a time the
  // save would not use.
  const draggedStart =
    transform == null
      ? null
      : timeAtColumnOffset({
          dayStartTime,
          yOffset: offset + transform.y,
          slotHeight,
          timeStep,
        });
  const shownStart = draggedStart ?? dayjs(timeoffStart).format("HH:mm");
  const shownEnd =
    draggedStart == null
      ? dayjs(timeoffEnd).format("HH:mm")
      : dayjs(`${date}T${draggedStart}`)
          .add(timeOffDuration, "minute")
          .format("HH:mm");

  const name = typeLabel(timeoff);
  const color = timeoff.typeColor ?? UNTYPED_COLOR;
  const icon = timeoff.typeEmoji ?? (
    <RiPauseCircleLine className={timeoffTimeClassName} />
  );

  const { note } = timeoff;
  const openTimeOff = useOpenTimeOff();

  return (
    <Tooltip
      content={name}
      isDisabled={window.innerWidth < 768}
      classNames={{
        base: "pointer-events-none",
      }}
      closeDelay={0}
      showArrow
      placement={"right"}
    >
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        // 90% to match an appointment's MAX_TOTAL_WIDTH, and for the same
        // reason: the block is clickable, so at full width it would swallow
        // every click meant for the empty slot behind it and there would be no
        // way to book over a time off. Hovering gives the width back.
        className="absolute left-0 flex w-[90%] cursor-pointer px-[3px] transition-[width] duration-150 hover:z-30 hover:w-full"
        style={{
          top: offset,
          height: height,
          containerType: "size",
          transform: transform
            ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
            : undefined,
          zIndex: transform ? 40 : undefined,
        }}
        onClick={(event) => {
          // The column underneath opens a new appointment on click, so the
          // block has to keep the event to itself.
          event.stopPropagation();
          openTimeOff(timeoff, date);
        }}
      >
        <div
          data-identifier="time-off-block"
          className="z-1 flex w-full flex-col items-center overflow-hidden rounded-xl border-2 border-l-5 pt-1"
          style={{
            borderColor: color,
            background: `white repeating-linear-gradient(
                    159.5deg,
                    transparent,
                    transparent 8px,
                    rgb(243, 244, 247) 8px,
                    rgb(243, 244, 247) 18px
                  )`,
          }}
        >
          <div className="flex w-full items-center justify-between px-1">
            <p className="overflow-hidden text-left text-xs whitespace-pre">
              {`${shownStart} - ${shownEnd}`}
            </p>
          </div>
          {/* The emoji rides with the name, not the time: a full range fills
              the line at this width and pushed the mark out of the block. */}
          <p
            className="flex w-full items-center gap-1 overflow-hidden pl-1 text-left text-xs font-semibold"
            style={{
              color,
            }}
          >
            <span className="shrink-0">{icon}</span>
            <span className="truncate">{name}</span>
          </p>
          <div className="w-full">
            {timeoff.note && (
              <p className="w-full pl-1 text-left text-xs font-medium text-[#969BA9]">
                {note}
              </p>
            )}
          </div>
        </div>
      </div>
    </Tooltip>
  );
}
